import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableFooter from '@material-ui/core/TableFooter';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import withStyles from '@material-ui/core/styles/withStyles';
import Tooltip from '@material-ui/core/Tooltip';
import FilteredIcon from './FilteredIcon';
import { isQuantitative } from '../../utils/stats';
import { nsOptions } from '../../i18n';
import FormattedValue from '../../utils/FormattedValue';
import DataOverviewModal from './DataOverviewModal';
import TextUtil from '../../utils/TextUtil';


const styles = (theme) => ({
  headCell: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2.fontSize,
    borderBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  cell: {
    borderBottom: 0,
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      paddingRight: 0,
    },
  },
  interactiveRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  clickableContent: {
    display: 'inline-block',
    borderBottom: `1px dotted ${theme.palette.text.secondary}`,
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderBottomColor: theme.palette.text.primary,
    },
  },
  filterIcon: {
    marginLeft: theme.spacing(0.5),
    marginBottom: theme.spacing(0.1),
    color: theme.palette.text.primary,
  },
  filteredIcon: {
    marginLeft: theme.spacing(0.5),
  },
  container: {
    ...theme.typography.body2,
    borderBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    width: 'fit-content',
    marginLeft: 0,
  },
});


@withTranslation('', nsOptions)
@withStyles(styles)
class TextStats extends React.Component {
    static propTypes = {
      // eslint-disable-next-line react/no-unused-prop-types
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      variableName: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      elementType: PropTypes.string,
      unit: PropTypes.string,
      count: PropTypes.number.isRequired,
      min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      median: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      mean: PropTypes.number,
      variance: PropTypes.number,
      stddev: PropTypes.number,
      interquartileRange: PropTypes.number,
      seriesNamesCounts: PropTypes.shape(),
      inModule: PropTypes.bool,
      t: PropTypes.func.isRequired,
      classes: PropTypes.shape().isRequired,
      filtersParams: PropTypes.shape(),
      isModule: PropTypes.bool,
      isLinkTarget: PropTypes.bool,
      project: PropTypes.shape().isRequired,
      showLoader: PropTypes.func,
      canUseAdvancedTools: PropTypes.bool.isRequired,
      setActive: PropTypes.func,
      unsetActive: PropTypes.func,
      chartData: PropTypes.arrayOf(PropTypes.shape()),
    };

    static defaultProps = {
      elementType: null,
      unit: '',
      min: null,
      max: null,
      median: null,
      mean: null,
      variance: null,
      stddev: null,
      interquartileRange: null,
      seriesNamesCounts: {},
      inModule: false,
      filtersParams: undefined,
      isModule: false,
      isLinkTarget: false,
      showLoader: () => {},
      setActive: () => {},
      unsetActive: () => {},
      chartData: [],
    };

    findChartIndex = (seriesName) => {
      const { chartData } = this.props;
      if (!chartData || chartData.length === 0) return -1;

      return chartData.findIndex((data) => data.name === seriesName || data.rawName === seriesName);
    };

    handleRowClick = (seriesName) => {
      const { setActive } = this.props;
      const chartIndex = this.findChartIndex(seriesName);

      if (chartIndex >= 0) {
        const pieSeriesName = 'value';
        setActive(chartIndex, pieSeriesName, true);
      }
    };

    render() {
      const {
        type, unit, count, min, max, median, mean, variance, stddev, interquartileRange, t, classes,
        seriesNamesCounts, inModule, variableName, isModule, isLinkTarget, id, filtersParams,
        project, elementType, showLoader, canUseAdvancedTools, setActive, chartData,
      } = this.props;

      const isInteractive = !isQuantitative(type) && !inModule
        && setActive && chartData && chartData.length > 0;

      let title;
      const stats = [];
      if (isQuantitative(type)) {
        title = t('stats:cross-entries-results');
        if (min !== null) {
          stats.push([t('stats:minimum'), <FormattedValue value={min} unit={unit} />]);
        }
        if (max !== null) {
          stats.push([t('stats:maximum'), <FormattedValue value={max} unit={unit} />]);
        }
        if (median !== null) {
          stats.push([t('stats:median'), <FormattedValue value={median} unit={unit} />]);
        }
        if (mean !== null) {
          stats.push([t('stats:mean'), <FormattedValue value={mean} unit={unit} />]);
        }
        if (variance !== null) {
          stats.push([t('stats:variance'), <FormattedValue value={variance} unit={unit ? `${unit}²` : null} />]);
        }
        if (stddev !== null) {
          stats.push([t('stats:standard-deviation'), <FormattedValue value={stddev} unit={unit} />]);
        }
        if (interquartileRange !== null) {
          stats.push([t('stats:interquartile-range'), <FormattedValue value={interquartileRange} unit={unit} />]);
        }
      } else {
        title = '';
        const sortedSeriesNamesCounts = Object.entries(seriesNamesCounts).sort(
          ([, aCount], [, bCount]) => {
            if (aCount < bCount) return 1;
            if (aCount > bCount) return -1;
            return 0;
          },
        );

        if (inModule) {
          const entriesCount = sortedSeriesNamesCounts
            .reduce((sum, [, valueCount]) => valueCount + sum, 0);

          sortedSeriesNamesCounts.forEach(([seriesName, seriesCount]) => {
            const name = seriesName || t('common:elements.default-labels.answer');
            const seriesLabel = `(${seriesCount}/${entriesCount} ${t('stats:entries')})`;
            stats.push([name,
              <>
                <FormattedValue value={seriesCount / entriesCount} isRatio />
                <span className="ml-1">{seriesLabel}</span>
              </>,
              false,
              seriesName,
            ]);
          });
        } else {
          sortedSeriesNamesCounts.forEach(([seriesName, seriesCount]) => {
            const name = seriesName || t('common:elements.default-labels.answer');
            const seriesLabel = `(${seriesCount}/${count})`;
            stats.push([name,
              <>
                <FormattedValue value={seriesCount / count} isRatio />
                <span className="ml-1">{seriesLabel}</span>
              </>,
              false,
              seriesName,
            ]);
          });
        }
      }

      if (stats.length === 0) {
        return null;
      }

      return (
        <Table size="small">
          { inModule ? (
            <TableHead>
              <TableRow>
                <TableCell colSpan={2} className={classes.headCell}>
                  {title}
                </TableCell>
              </TableRow>
            </TableHead>
          ) : null }
          <TableBody>
            {stats.map(([name, value, filtered, originalSeriesName]) => (
              <TableRow
                key={name}
                className={isInteractive ? classes.interactiveRow : ''}
                onClick={isInteractive
                  ? () => this.handleRowClick(originalSeriesName || name) : undefined}
                onMouseEnter={isInteractive
                  ? () => {
                    const chartIndex = this.findChartIndex(originalSeriesName || name);
                    if (chartIndex >= 0) {
                      const pieSeriesName = 'value';
                      setActive(chartIndex, pieSeriesName, false); // false = hover, pas click
                    }
                  }
                  : undefined}
                onMouseLeave={isInteractive
                  ? () => {
                    this.props.unsetActive();
                  }
                  : undefined}
              >
                <TableCell className={classes.cell}>
                  <Tooltip
                    title={
                        (() => {
                          if (isInteractive) return t('stats:click-to-select');
                          if (name.length > 30) return name;
                          return '';
                        })()
                    }
                    placement="top"
                    enterTouchDelay={50}
                  >
                    <span className={isInteractive ? classes.clickableContent : ''}>
                      {TextUtil.truncate(name, 17)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  <Tooltip
                    title={isInteractive ? t('stats:click-to-select') : ''}
                    placement="top"
                    enterTouchDelay={50}
                  >
                    <span className={isInteractive ? classes.clickableContent : ''}>
                      { value }
                    </span>
                  </Tooltip>
                  { filtered ? <FilteredIcon className={classes.filteredIcon} /> : null }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2} className={classes.cell}>
                <DataOverviewModal
                  id={id}
                  name={variableName}
                  isModule={isModule}
                  count={count}
                  filtersParams={filtersParams}
                  project={project}
                  isLinkTarget={isLinkTarget}
                  elementType={elementType}
                  showLoader={showLoader}
                  canUseAdvancedTools={canUseAdvancedTools}
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      );
    }
}

export default TextStats;
